<script setup lang="ts">
import { useI18n } from 'vue-i18n'

/**
 * Lien d'évitement : « aller au contenu ».
 *
 * Invisible jusqu'à ce qu'il reçoive le focus, donc gratuit pour la souris —
 * mais c'est le PREMIER élément atteint par la touche Tab. Sans lui, un
 * utilisateur au clavier traverse la barre supérieure et les quatre entrées du
 * menu latéral **à chaque changement d'écran** avant d'atteindre le contenu.
 * Sur une application qu'on utilise toute la journée, ce sont des milliers de
 * tabulations par semaine.
 *
 * La cible `#contenu-principal` est portée par le `<main>` de `AppLayout`, qui
 * doit être focalisable (`tabindex="-1"`) : sans cet attribut, la plupart des
 * navigateurs déplacent le défilement sans déplacer le focus, et la tabulation
 * suivante repart du début de la page. Le saut paraît alors ne rien faire.
 */
const { t } = useI18n()
</script>

<template>
  <a class="skip-link" href="#contenu-principal">{{ t('a11y.skipToContent') }}</a>
</template>

<style scoped>
.skip-link {
  position: fixed;
  top: var(--space-2);
  left: var(--space-2);
  z-index: var(--z-tooltip);
  padding: var(--space-2) var(--space-4);
  background: var(--color-primary);
  color: var(--color-text-inverse);
  border-radius: var(--radius-md);
  font-weight: var(--font-medium);
  box-shadow: var(--shadow-lg);
  /* Sorti de l'écran plutôt que masqué : un `display:none` le rendrait
     inatteignable au clavier, ce qui annulerait tout l'intérêt. */
  transform: translateY(calc(-100% - var(--space-4)));
  transition: transform var(--duration-fast) var(--ease-out);
}

.skip-link:focus-visible {
  transform: translateY(0);
}
</style>
